import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import type { Cart } from '@getjust/gateway';

import { CART_QUERY_KEY } from '$src/constants';
import { useROFormMessageAtom } from '$src/hooks/state';
import { writeCachedCart } from '$src/utils/cartCache';

const DEBOUNCE_MS = 200;

/**
 * Mount once at the InitWrapper level. Subscribes to the TanStack
 * QueryCache and writes the latest `Cart` JSON to
 * `localStorage[just_cart_${shopId}]` whenever the cart query updates.
 *
 * Debounced 200 ms so the optimistic + landing + invalidation triple
 * during a mutation collapses to one storage write. Idempotent on
 * unmount.
 */
export function useCartCache(): void {
  const queryClient = useQueryClient();
  const message = useROFormMessageAtom();
  const shopId = message?.shopId;

  useEffect(() => {
    if (!shopId) return;
    if (typeof window === 'undefined') return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let latestCart: Cart | undefined;

    const flush = () => {
      timer = null;
      if (latestCart) {
        try {
          writeCachedCart(shopId, latestCart);
        } catch (err) {
          // setStorage from commons already swallows storage errors;
          // this only catches an unexpected JSON.stringify failure on a
          // non-serializable cart (e.g., circular ref) — surface it
          // rather than silently dropping cache writes forever.
          console.error('[useCartCache] writeCachedCart failed', err);
        }
        latestCart = undefined;
      }
    };

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      // We only care about updates to the cart query family. Skip
      // creates with no data, errors, etc.
      if (event.type !== 'updated') return;
      const key = event.query.queryKey;
      if (!Array.isArray(key) || key[0] !== CART_QUERY_KEY) return;
      const data = event.query.state.data as Cart | undefined;
      if (!data?.id) return;
      latestCart = data;
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, DEBOUNCE_MS);
    });

    return () => {
      unsubscribe();
      if (timer) {
        clearTimeout(timer);
        // Flush pending on unmount so we don't lose the last update on
        // a navigation away from the iframe.
        flush();
      }
    };
  }, [queryClient, shopId]);
}
